import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo,
  SyntheticEvent,
  useCallback,
} from "react";

import {
  Button,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Modal,
  Segment,
} from "semantic-ui-react";

import {
  State as SharedLabResultSequence,
  BTN_ACTS,
} from "react-lib/apps/MEH/sequence/SharedLabResult";

import { Table } from "react-lib/frameworks/Table";
import { base64toBlob } from "react-lib/apps/HISV3/common/CommonInterface";
import DateTextBox from "react-lib/component-js/common/DateTextBox";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import { PDFDocument, degrees } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import moment from "moment";
import axios from "axios";

// scss
import "./CardSharedLabResult.scss";

const CARD_KEY = "CardSharedLabResult";

type CardSharedLabResultProps = {
  setProp: any;
  onEvent: any;
  runSequence: any;
  masterOptions?: Record<string, any>;
  SharedLabResultSequence: SharedLabResultSequence["SharedLabResultSequence"];
  buttonLoadCheck?: Record<string, any>;
  viewIndex?: number;
  languageUX?: string;
};

const SHARED_LAB_RESULT = "SharedLabResult";

const CardSharedLabResult = (props: CardSharedLabResultProps) => {
  const [pdfBlob, setPdfBlob] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    props.runSequence({
      sequence: "SharedLabResult",
      restart: true,
    });
  }, [props.viewIndex]);

  useEffect(() => {
    setPdfBlob("");
    setZoom(100);
    handleSetDocumentImage(props.SharedLabResultSequence?.selectedDocuments);
  }, [props.SharedLabResultSequence?.selectedDocuments]);

  const generateDocumentImage = useCallback(async (selectedRow: any) => {
    if (
      !selectedRow ||
      !selectedRow.scanned_documents ||
      selectedRow.scanned_documents.length === 0
    ) {
      return "";
    }

    const pdfDoc = await PDFDocument.create();

    try {
      const fetchImages = selectedRow.scanned_documents.map((item: any) =>
        axios
          .get(item.document_image, {
            responseType: "arraybuffer",
          })
          .then((res) => ({
            data: res.data,
            item,
          })),
      );

      const imageResponses = await Promise.all(fetchImages);

      for (const { data, item } of imageResponses) {
        try {
          const jpgImage = await pdfDoc.embedJpg(data);

          const jpgDims = jpgImage.scale(0.5);

          const page = pdfDoc.addPage([jpgDims.width, jpgDims.height]);

          page.drawImage(jpgImage, {
            x: 0,
            y: 0,
            width: jpgDims.width,
            height: jpgDims.height,
            opacity: 1,
            rotate: degrees(0),
          });
        } catch (error) {
          console.error(`Failed to process image ${item.document_image}:`, error);
        }
      }

      return await pdfDoc.saveAsBase64({
        dataUri: true,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);

      return "";
    }
  }, []);

  const handleSetDocumentImage = useCallback(
    async (selectedRow: any) => {
      // reset ก่อน
      setPdfBlob("");
      setNumPages(0);
      setSelectedPage(1);
      setZoom(100);

      const pdf = await generateDocumentImage(selectedRow);

      const blob = pdf ? URL.createObjectURL(base64toBlob(pdf)) : "";

      setPdfBlob(blob);
    },
    [generateDocumentImage],
  );

  const divisionOptions = useMemo(() => {
    const options = props.masterOptions?.division || [];

    return [
      {
        key: 0,
        text: "ทั้งหมด (All)",
        value: "ALL",
      },
      ...options,
    ];
  }, [props.masterOptions]);

  const sharedLabResultData = useMemo(() => {
    return (props.SharedLabResultSequence?.sharedLabResult || []).map(
      (item: any, index: number) => {
        const documentDate = moment(item?.document_date).format("DD/MM/YYYY");

        const totalPage =
          item?.scanned_documents?.reduce(
            (sum: number, scannedItem: any) => sum + (scannedItem?.page || 0),
            0,
          ) || 0;

        return {
          ...item,

          document_date: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {documentDate || ""}
            </div>
          ),

          id: (
            <div
              style={{
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {item?.document_no || ""}
            </div>
          ),

          page: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  background: "#E0E6EE",
                  padding: "2px 10px",
                  borderRadius: 12,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                {totalPage || 0} หน้า
              </div>
            </div>
          ),
        };
      },
    );
  }, [props.SharedLabResultSequence?.sharedLabResult]);

  const handleChangeDate = (type: "startDate" | "endDate", date: string) => {
    props.setProp(`SharedLabResultSequence.sharedLabResultFilter.${type}`, date);
  };

  const handleChangeFilter = (
    e: SyntheticEvent | null,
    data: {
      checked?: boolean;
      name: string;
      value: boolean | string;
    },
  ) => {
    const value = data.checked === undefined ? data.value : data.checked;

    props.setProp(`SharedLabResultSequence.sharedLabResultFilter.${data.name}`, value);
  };

  const handleSelectedPDF = (state: any, rowInfo: any) => {
    const selectedDocuments = props.SharedLabResultSequence?.sharedLabResult?.find((item: any) => {
      return item.document_no === rowInfo?.original?.document_no;
    });

    const isSelected =
      rowInfo?.original?.document_no &&
      rowInfo?.original?.document_no ===
        props.SharedLabResultSequence?.selectedDocuments?.document_no;

    return {
      style: {
        backgroundColor: isSelected ? "var(--primary-select-background)" : undefined,
      },

      onClick: () => {
        console.log("Selected row:", selectedDocuments);

        props.setProp("SharedLabResultSequence.selectedDocuments", selectedDocuments);
      },
    };
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "SharedLabResult",
      action: "SEARCH",
    });
  };

  const handleClear = () => {
    props.setProp("SharedLabResultSequence.selectedDocuments", null);

    props.runSequence({
      sequence: "SharedLabResult",
      action: "CLEAR_DATA",
    });
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  return (
    <div className="CardSharedLabResult">
      <Form className="shared-lab-form">
        <FormGroup className="shared-lab-filter-group">
          <FormField width={6}>
            <div className="shared-lab-label">
              <Icon className="calendar outline" />
              <label>วันที่ Shared Lab Result (เริ่มต้น - สิ้นสุด)</label>
            </div>

            <div className="shared-lab-date-range">
              <DateTextBox
                value={props.SharedLabResultSequence?.sharedLabResultFilter?.startDate}
                onChange={(date: string) => handleChangeDate("startDate", date)}
              />

              <div>ถึง</div>

              <DateTextBox
                value={props.SharedLabResultSequence?.sharedLabResultFilter?.endDate}
                onChange={(date: string) => handleChangeDate("endDate", date)}
              />
            </div>
          </FormField>

          <FormField width={3}>
            <div className="shared-lab-label">
              <Icon className="hospital" />
              <label>From Division</label>
            </div>

            <Dropdown
              fluid
              name="division"
              onChange={handleChangeFilter}
              options={divisionOptions}
              search
              selection
              value={props.SharedLabResultSequence?.sharedLabResultFilter?.division || ""}
            />
          </FormField>

          <FormField width={3}>
            <div className="shared-lab-label">
              <Icon className="file alternate" />
              <label>เลขที่เอกสาร</label>
            </div>

            <Input fluid name="documentNo" onChange={handleChangeFilter} />
          </FormField>

          <FormField width={4} className="shared-lab-action-field">
            <label />

            <div className="shared-lab-actions">
              <ButtonLoadCheck
                setProp={props.setProp}
                onClick={handleSearch}
                paramKey={BTN_ACTS.SEARCH}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
                className="shared-lab-search-button"
                icon="search"
                size="small"
                title="ค้นหา"
              />

              <ButtonLoadCheck
                setProp={props.setProp}
                onClick={handleClear}
                className="shared-lab-clear-button"
                icon="undo"
                size="small"
                title="ล้าง"
              />
            </div>
          </FormField>
        </FormGroup>

        <FormGroup className="shared-lab-content-group">
          {/* Left */}
          <FormField width={7} className="shared-lab-panel">
            <div className="shared-lab-panel-header">
              <div className="shared-lab-panel-title">
                <Icon className="folder open" />
                <label>รายการชุดผลตรวจ Lab</label>
              </div>

              <label className="shared-lab-count">
                {`${props.SharedLabResultSequence?.sharedLabResult?.length || 0} รายการ`}
              </label>
            </div>

            <div className="shared-lab-table-container">
              <Table
                className="--rt-tbody-overflow-y-hidden"
                headers={`วันที่เอกสาร,เลขที่เอกสาร,From Division,จำนวน`}
                keys="document_date,id,scan_division_name,page"
                widths="auto,auto,^120px,^100px"
                data={sharedLabResultData}
                getTrProps={handleSelectedPDF}
                showPagination={false}
              />
            </div>
          </FormField>

          {/* Right */}
          <FormField width={9} className="shared-lab-panel">
            <div className="shared-lab-panel-header">
              <div>
                <div className={`shared-lab-document-info ${!pdfBlob ? "hidden" : ""}`}>
                  <label>
                    {props.SharedLabResultSequence?.selectedDocuments?.document_no || ""}
                  </label>

                  {props.SharedLabResultSequence?.selectedDocuments?.document_no && (
                    <label className="shared-lab-division">
                      {`(${props.SharedLabResultSequence?.selectedDocuments?.scan_division_name})`}
                    </label>
                  )}

                  <label>{`- (หน้า ${selectedPage}/${numPages})`}</label>
                </div>
              </div>

              <div className="shared-lab-pdf-actions">
                <ButtonLoadCheck
                  setProp={props.setProp}
                  onClick={handleZoomIn}
                  className="shared-lab-icon-button"
                  icon="zoom-in"
                  size="small"
                />

                <ButtonLoadCheck
                  setProp={props.setProp}
                  onClick={handleZoomOut}
                  className="shared-lab-icon-button"
                  icon="zoom-out"
                  size="small"
                />

                <ButtonLoadCheck
                  setProp={props.setProp}
                  onClick={() => {
                    setIsFullscreen(true);
                  }}
                  className="shared-lab-icon-button"
                  icon="expand"
                  size="small"
                />
              </div>
            </div>

            <Segment id="container" color="grey" inverted className="shared-lab-pdf-container">
              {pdfBlob ? (
                <PDFPreview
                  file={pdfBlob}
                  numPages={numPages}
                  selectedPage={selectedPage}
                  zoom={zoom}
                  setNumPages={setNumPages}
                  setSelectedPage={setSelectedPage}
                  setZoom={setZoom}
                />
              ) : (
                <div className="shared-lab-empty-preview">เลือกเอกสารเพื่อแสดงไฟล์</div>
              )}
            </Segment>
          </FormField>
        </FormGroup>
      </Form>

      <Modal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        className="shared-lab-fullscreen-modal"
      >
        <Icon
          name="close"
          onClick={() => setIsFullscreen(false)}
          className="shared-lab-close-fullscreen"
        />

        {isFullscreen && pdfBlob && (
          <PDFPreview
            key={`fullscreen-${pdfBlob}`}
            file={pdfBlob}
            numPages={numPages}
            selectedPage={selectedPage}
            zoom={34}
            setNumPages={setNumPages}
            setSelectedPage={setSelectedPage}
            setZoom={setZoom}
            isFullscreen
          />
        )}
      </Modal>
    </div>
  );
};

CardSharedLabResult.displayName = "CardSharedLabResult";

export default memo(CardSharedLabResult);

const PDFPreview = ({
  file,
  numPages,
  selectedPage,
  zoom,
  isFullscreen,
  setNumPages,
  setSelectedPage,
  setZoom,
}: {
  file: string;
  numPages: number;
  selectedPage: number;
  zoom: number;
  isFullscreen?: boolean;
  setNumPages: React.Dispatch<React.SetStateAction<number>>;
  setSelectedPage: React.Dispatch<React.SetStateAction<number>>;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [pages, setPages] = useState<string[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfRef = useRef<any>(null);

  useEffect(() => {
    if (!file) {
      setPages([]);
      setNumPages(0);
      setSelectedPage(1);
      setZoom(100);
      pdfRef.current = null;
      return;
    }

    let cancelled = false;

    const loadPDF = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(file);

        const pdf = await loadingTask.promise;

        if (cancelled) return;

        pdfRef.current = pdf;

        setNumPages(pdf.numPages);

        if (!isFullscreen) {
          setSelectedPage(1);
        }

        const thumbnails: string[] = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          if (cancelled) return;

          const page = await pdf.getPage(pageNumber);

          const viewport = page.getViewport({
            scale: 0.25,
          });

          const canvas = document.createElement("canvas");

          const context = canvas.getContext("2d");

          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;

          thumbnails.push(canvas.toDataURL("image/jpeg", 0.7));
        }

        if (!cancelled) {
          setPages(thumbnails);
        }
      } catch (error) {
        console.error("Failed to load PDF:", error);
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    const renderSelectedPage = async () => {
      const pdf = pdfRef.current;
      const canvas = canvasRef.current;

      if (!pdf || !canvas || !selectedPage) {
        return;
      }

      try {
        const page = await pdf.getPage(selectedPage);

        // ใช้ scroll container เป็นตัวคำนวณ
        const viewer = canvas.closest(".pdf-viewer");

        if (!viewer) return;

        const originalViewport = page.getViewport({
          scale: 1,
        });

        const availableWidth = viewer.clientWidth - (isFullscreen ? 10 : 40);

        // ขนาดที่พอดีกับ viewer
        const fitScale = availableWidth / originalViewport.width;

        // zoom เป็นตัวคูณจาก fitScale
        const scale = fitScale * (zoom / 100);

        const viewport = page.getViewport({
          scale: Math.max(scale, 0.1),
        });

        const context = canvas.getContext("2d");

        if (!context) return;

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        // clear canvas ก่อน render
        context.clearRect(0, 0, canvas.width, canvas.height);

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;
      } catch (error) {
        console.error("Failed to render selected page:", error);
      }
    };

    renderSelectedPage();
  }, [selectedPage, zoom, pages]);

  const handleSelectPage = (pageNumber: number) => {
    setSelectedPage(pageNumber);
  };

  const handlePrevious = () => {
    setSelectedPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setSelectedPage((prev) => Math.min(prev + 1, numPages));
  };

  return (
    <div className={`pdf-viewer ${isFullscreen ? "pdf-viewer-fullscreen" : ""}`}>
      {/* PDF */}
      <div className={!isFullscreen ? "pdf-viewer-content" : ""}>
        <div className="pdf-viewer-canvas-wrapper">
          <canvas ref={canvasRef} className="pdf-viewer-canvas" />
        </div>
      </div>

      {/* Page Control */}
      {numPages > 0 && !isFullscreen && (
        <div className="pdf-viewer-page-control">
          <button type="button" onClick={handlePrevious} disabled={selectedPage === 1}>
            ‹
          </button>

          <span>
            {selectedPage} / {numPages}
          </span>

          <button type="button" onClick={handleNext} disabled={selectedPage === numPages}>
            ›
          </button>
        </div>
      )}

      {/* Thumbnail */}
      {numPages > 0 && !isFullscreen && (
        <div className="pdf-viewer-thumbnails">
          {pages.map((thumbnail, index) => {
            const pageNumber = index + 1;
            const isSelected = selectedPage === pageNumber;

            return (
              <div
                key={pageNumber}
                onClick={() => handleSelectPage(pageNumber)}
                className={`pdf-viewer-thumbnail ${isSelected ? "selected" : ""}`}
              >
                <img src={thumbnail} alt={`Page ${pageNumber}`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
